/*
 * Red Ants Pants — Fit Quiz sizing logic (single source of truth)
 * ----------------------------------------------------------------
 * This file is the ONE place the size/length recommendation is decided.
 * It is loaded by:
 *   - sections/fitness-quiz.liquid  (the live quiz on the site)
 *   - assets/fit-quiz-test.html     (the verification harness the team runs)
 *
 * Because both load THIS file, anything the test page passes is exactly
 * what a customer gets. If you change a rule, change it here only.
 *
 * Everything is exposed on window.RAPFitQuiz. All functions are pure:
 * given the same `answers` object they always return the same result and
 * touch no DOM, so they can be swept across every input combination.
 */
(function (global) {
  'use strict';

  // Official Red Ants Pants size charts (in inches).
  var STRAIGHT_CHART = [
    { size: 0,  waist: 25,   hip: 35 },
    { size: 2,  waist: 26,   hip: 36 },
    { size: 4,  waist: 27,   hip: 37 },
    { size: 6,  waist: 28,   hip: 38 },
    { size: 8,  waist: 29.5, hip: 39.5 },
    { size: 10, waist: 31,   hip: 41 },
    { size: 12, waist: 32.5, hip: 42.5 },
    { size: 14, waist: 34.5, hip: 44.5 },
    { size: 16, waist: 36.5, hip: 46.5 },
    { size: 18, waist: 39.5, hip: 49.5 },
    { size: 20, waist: 42.5, hip: 52.5 },
    { size: 22, waist: 45.5, hip: 55.5 },
    { size: 24, waist: 48.5, hip: 58.5 }
  ];
  var CURVY_CHART = [
    { size: 0,  waist: 26,   hip: 38.5 },
    { size: 2,  waist: 27,   hip: 39.5 },
    { size: 4,  waist: 28,   hip: 40.5 },
    { size: 6,  waist: 29,   hip: 41.5 },
    { size: 8,  waist: 30.5, hip: 43   },
    { size: 10, waist: 32,   hip: 44.5 },
    { size: 12, waist: 33.5, hip: 46   },
    { size: 14, waist: 35.5, hip: 48   },
    { size: 16, waist: 37.5, hip: 50   },
    { size: 18, waist: 40.5, hip: 53   },
    { size: 20, waist: 43.5, hip: 56   },
    { size: 22, waist: 46.5, hip: 59   },
    { size: 24, waist: 49.5, hip: 62   }
  ];

  // Length bands. Written as explicit, ordered, INCLUSIVE ranges so there
  // are no gaps and no off-by-one at the edges. The first band whose test
  // passes wins, so each band only needs its UPPER bound (`maxIn` = highest
  // value still in this band, inclusive). The last band is the catch-all.
  //
  // Inseam wins when the customer gives one. Otherwise we fall back to height.
  // Both scales use the SAME band names so the result reads consistently.
  //
  //   Inseam (inches):  Short 30-31  |  Regular 32-34  |  Long 35   (official chart)
  //   Height (inches):  Short <64    |  Regular 64-68  |  Long >=69 (5'9"+)
  //
  // Inseam bands match the "Inseam options (in)" column of the published size
  // chart exactly: S: 30–31 / R: 32–34 / L: 35. The input accepts 24-36, so we
  // extrapolate the open ends — an inseam under 30 reads Short, over 35 reads
  // Long (each band only needs its inclusive upper bound; first match wins).
  //
  // 5'9" = 69 inches -> Long. (Long now starts at 5'9" and above, per the
  // client's confirmed fit rule.)
  var INSEAM_BANDS = [
    { label: 'Short',   maxIn: 31 },
    { label: 'Regular', maxIn: 34 },
    { label: 'Long',    maxIn: Infinity }
  ];
  var HEIGHT_BANDS = [
    { label: 'Short',   maxIn: 63 },   // under 5'4"
    { label: 'Regular', maxIn: 68 },   // 5'4" – 5'8"
    { label: 'Long',    maxIn: Infinity } // 5'9" and up
  ];

  function bandFor(value, bands) {
    for (var i = 0; i < bands.length; i++) {
      if (value <= bands[i].maxIn) return bands[i].label;
    }
    return bands[bands.length - 1].label;
  }

  // Convert the height dropdown answers (feet + inches) to total inches.
  // Returns 0 when no usable height was given.
  function heightToInches(ft, inches) {
    var inExtra = parseInt(inches, 10) || 0;
    if (ft === '6+') return 75;        // the "6'3"+" bucket
    if (ft) return parseInt(ft, 10) * 12 + inExtra;
    return 0;
  }

  // Decide the length label from the raw answers.
  function lengthFromAnswers(answers) {
    var inseam = parseInt(answers.inseam, 10);
    if (inseam) return bandFor(inseam, INSEAM_BANDS);

    var totalInches = heightToInches(answers['height-ft'], answers['height-in']);
    if (!totalInches) return 'Regular'; // no height given -> safe default
    return bandFor(totalInches, HEIGHT_BANDS);
  }

  // Given a measurement, pick the size from a chart.
  //
  // The fit preference (Q4) is a TIE-BREAKER, not a size bump — the question's
  // own helper text on the page says "Helpful if you're right between sizes":
  //   - the measurement lands exactly on a chart row -> that size, either way
  //   - it falls between two rows -> Looser rounds UP, More Fitted rounds DOWN
  //   - no preference given          -> round UP (the pant has to clear the body)
  // So the preference can never move someone off a size the chart already
  // agreed on. Since sizes step by 2, rounding up IS the "one size up" the
  // Looser answer asks for; adding a further +2 on top of it would double-count
  // and is what produced the reported "wears a 10, was shown a 14".
  function pickSizeForMeasurement(value, key, fitPref, chart) {
    if (value == null || isNaN(value)) return null;
    var lower = null, upper = null;
    for (var i = 0; i < chart.length; i++) {
      var target = chart[i][key];
      if (target === value) return chart[i].size;
      if (target < value) lower = chart[i];
      else { upper = chart[i]; break; }
    }
    if (!lower) return upper.size;
    if (!upper) return lower.size;
    return (fitPref === 'fitted') ? lower.size : upper.size;
  }

  // Look up size from waist + hip in the given chart. Take the larger of
  // the two so the pant accommodates both. Returns null if neither is set.
  function sizeFromMeasurements(waist, hip, fitPref, chart) {
    var w = pickSizeForMeasurement(waist, 'waist', fitPref, chart);
    var h = pickSizeForMeasurement(hip, 'hip', fitPref, chart);
    if (w == null && h == null) return null;
    if (w == null) return h;
    if (h == null) return w;
    return Math.max(w, h);
  }

  // True if a given waist/hip falls outside the chart entirely (below the
  // smallest row or above the largest). Such values get floored/capped to an
  // end size, which is a red flag that the input may be wrong.
  function measurementOutOfRange(waist, hip, chart) {
    var first = chart[0], last = chart[chart.length - 1];
    if (!isNaN(waist) && (waist < first.waist || waist > last.waist)) return true;
    if (!isNaN(hip) && (hip < first.hip || hip > last.hip)) return true;
    return false;
  }

  // How far apart (in numeric size units) the measurement-based size and the
  // stated-size baseline may be before we treat the inputs as contradictory.
  // Sizes step by 2, so 2 = one full size, which is normal cross-brand vanity
  // sizing and is accepted silently. Anything wider than that — telling someone
  // who wears a 10 that she's a 14 — is not a recommendation we make quietly:
  // we show her usual size and ask her to double-check the tape.
  var SIZE_CONTRADICTION_GAP = 2;

  // The full recommendation. `answers` is the same flat object the quiz
  // collects from its inputs (size, shape, height-ft, height-in,
  // fit-preference, waist, hip, inseam).
  //
  // Returns { size, fit, length, shape, fitPref, usedMeasurements, recheck }.
  // `recheck` is null when we're confident, or { reason } when the inputs
  // disagree and the customer should double-check (the UI shows a notice).
  function computeResult(answers) {
    answers = answers || {};
    var size = parseInt(answers.size, 10);
    var shape = answers.shape || 'straight';
    var fitPref = answers['fit-preference'];
    var waist = parseFloat(answers.waist);
    var hip = parseFloat(answers.hip);

    var isVeryCurvy = (shape === 'very-curvy');
    var chart = isVeryCurvy ? CURVY_CHART : STRAIGHT_CHART;

    // The stated-size baseline (Q1) — and, when Q5 is skipped, the final answer.
    // Q1 is the usual women's size, assumed straight-fit equivalent; Very Curvy
    // sizes down by 2 to land on the Curvy pattern.
    var statedBase = isNaN(size) ? NaN : (isVeryCurvy ? Math.max(0, size - 2) : size);

    // Measurements are only trusted when BOTH waist and hip are given — a
    // single number isn't enough to override the stated size. The customer's
    // own fit preference is passed straight through so it breaks between-row
    // ties here and nowhere else (see pickSizeForMeasurement).
    var hasBothMeasurements = !isNaN(waist) && !isNaN(hip);
    var measSize = hasBothMeasurements
      ? sizeFromMeasurements(waist, hip, fitPref, chart)
      : null;

    // Choose the size and detect contradictions. `measSize` is already the
    // final measured answer (the preference was applied in the lookup), so the
    // gap below is the real gap the customer would have been shown — nothing is
    // added to it afterwards.
    var recheck = null;
    var recSize, usedMeasurements;

    if (measSize != null) {
      var outOfRange = measurementOutOfRange(waist, hip, chart);
      var contradictsStated = !isNaN(statedBase) &&
        Math.abs(measSize - statedBase) > SIZE_CONTRADICTION_GAP;

      if (outOfRange || contradictsStated) {
        // The inputs don't agree. Flag it, and fall back to the stated size
        // (the safer signal) when we have one; otherwise use the clamped
        // measurement so we still show something.
        recheck = { reason: outOfRange ? 'measurement-out-of-range' : 'measurement-vs-size' };
        if (!isNaN(statedBase)) { recSize = statedBase; usedMeasurements = false; }
        else { recSize = measSize; usedMeasurements = true; }
      } else {
        recSize = measSize;
        usedMeasurements = true;
      }
    } else if (!isNaN(statedBase)) {
      recSize = statedBase;
      usedMeasurements = false;
    } else {
      recSize = NaN;
      usedMeasurements = false;
    }

    // Fit label tracks which product we show (Curvy product only for
    // very-curvy; Straight and A Little Curvy share the straight-cut product).
    var fit = isVeryCurvy ? 'Curvy' : 'Straight';

    // Keep the answer on the chart: sizes run 0-24 in steps of 2.
    if (!isNaN(recSize)) {
      recSize = Math.min(24, Math.max(0, recSize));
    }

    // NOTE: there is deliberately no size bump here. On the stated-size path
    // (Q5 skipped) there are no two sizes to choose between, so the fit
    // preference has no tie to break and the customer is shown the size she
    // told us she wears. On the measured path the preference has already been
    // applied inside the chart lookup above.

    // Length (inseam if given, otherwise height). See band tables above.
    var length = lengthFromAnswers(answers);

    return {
      size: recSize,
      fit: fit,
      length: length,
      shape: shape,
      fitPref: fitPref,
      usedMeasurements: usedMeasurements,
      recheck: recheck
    };
  }

  global.RAPFitQuiz = {
    STRAIGHT_CHART: STRAIGHT_CHART,
    CURVY_CHART: CURVY_CHART,
    INSEAM_BANDS: INSEAM_BANDS,
    HEIGHT_BANDS: HEIGHT_BANDS,
    heightToInches: heightToInches,
    bandFor: bandFor,
    lengthFromAnswers: lengthFromAnswers,
    pickSizeForMeasurement: pickSizeForMeasurement,
    sizeFromMeasurements: sizeFromMeasurements,
    computeResult: computeResult
  };
})(typeof window !== 'undefined' ? window : this);
